import {
  BookingStatus,
  type Entitlement,
  ProductCategory,
} from '@collinsonx/utils/generatedTypes/graphql';
import { getItem } from '@lib';
import { BOOKING_MODE } from 'config/constants';
import { usePerVisitLimit } from 'config/features';
import {
  ELEVATED_LOUNGES,
  getSourceCodesWithElevatedPermission,
  getSourceCodesWithStandardPermission,
} from 'config/productDefaultConfig';
import { BOOKING_MODE_STATE } from 'config/storage';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import useBooking from 'hooks/useBooking';
import { formatEntitlementsDate } from 'utils/entitlements';

import useBookingDetails from './useBookingDetails';
import usePayload from './usePayload';
import useProduct from './useProduct';

dayjs.extend(utc);
dayjs.extend(timezone);

const AMEX_RELEASE_DATE = dayjs.tz('2025-07-30 09:30', 'Europe/London');

const useGetEntitlements = () => {
  const { bookingDetailsData, loadingBookingDetails } = useBookingDetails();
  const {
    booking,
    entitlements: bookingEntitlements,
    isLoadingEntitlements,
  } = useBooking();

  const mode = getItem(BOOKING_MODE_STATE);

  const {
    category,
    entitlements: valuationEntitlements,
    loungeCode,
  } = useProduct();
  const { membership } = usePayload();

  const { selectedEntitlementsCount, taxableGuestsCount } = booking;

  const fetchedEntitlementsForProduct =
    valuationEntitlements ?? bookingEntitlements ?? [];

  // Cancelling refunds the booking's entitlements back to the consumer, so they
  // are already part of the freshly fetched pool. Counting them again would
  // overstate the balance.
  const isCancelledBooking =
    bookingDetailsData?.getBookingByID?.status === BookingStatus.Cancelled;

  const previouslyUsedEntitlements = isCancelledBooking
    ? []
    : bookingDetailsData?.getBookingByID?.entitlements?.filter(
        (item: Entitlement) => !item.refunded
      ) || [];

  const entitlements = fetchedEntitlementsForProduct.concat(
    previouslyUsedEntitlements
  );
  const hasUnlimitedEntitlements = entitlements[0]?.isUnlimited || false;

  const entitlemenstExpiryDate = formatEntitlementsDate(
    entitlements[0]?.expiryDate
  );

  const entitlementsCount = entitlements?.length ?? 0;

  const hasEntitlements = entitlementsCount > 0;

  const usedEntitlementsCount =
    bookingDetailsData?.getBookingByID?.usedEntitlementsCount ?? 0;
  const bookingCreationDate =
    bookingDetailsData?.getBookingByID?.createdAt ?? 0;

  const availableEntitlements =
    (hasUnlimitedEntitlements || isCancelledBooking
      ? 0
      : usedEntitlementsCount) + fetchedEntitlementsForProduct.length;

  const remainingEntitlementsCount = hasUnlimitedEntitlements
    ? 0
    : Math.max(0, availableEntitlements - selectedEntitlementsCount);

  const perVisitLimit = entitlements[0]?.perVisitLimit;

  const maxEntitlementsCount = !hasUnlimitedEntitlements
    ? Math.min(taxableGuestsCount, availableEntitlements)
    : usePerVisitLimit
    ? Math.min(
        taxableGuestsCount,
        typeof perVisitLimit === 'number' && perVisitLimit >= 0
          ? perVisitLimit
          : taxableGuestsCount
      )
    : taxableGuestsCount;

  const safeMaxEntitlementsCount = Math.max(0, maxEntitlementsCount);

  const fetchingEntitlementsData = loadingBookingDetails;

  const isElevatedLounge = ELEVATED_LOUNGES.includes(loungeCode);
  const isFastTrackProduct = category === ProductCategory.FastTrack;

  const membershipPrefix =
    membership?.membershipPlan?.membershipPrefix?.slice(0, -1) ?? '';

  const hasStandardPermission =
    getSourceCodesWithStandardPermission().includes(membershipPrefix);

  const hasElevatedPermission =
    getSourceCodesWithElevatedPermission().includes(membershipPrefix);

  const hasStandardOrElevatedPermission =
    hasStandardPermission || hasElevatedPermission;

  const hasPermissionsForEntitlements = isElevatedLounge
    ? hasElevatedPermission
    : hasStandardOrElevatedPermission;

  const disableEntitlements =
    mode === BOOKING_MODE.EDIT &&
    dayjs(bookingCreationDate).isBefore(AMEX_RELEASE_DATE) &&
    !usedEntitlementsCount;

  return {
    availableEntitlements,
    disableEntitlements,
    entitlemenstExpiryDate,
    entitlements: disableEntitlements ? [] : entitlements,
    entitlementsCount: disableEntitlements ? 0 : entitlementsCount,
    fetchingEntitlementsData,
    hasEntitlements,
    hasPermissionsForEntitlements,
    hasUnlimitedEntitlements: disableEntitlements
      ? false
      : hasUnlimitedEntitlements,
    isFastTrackProduct,
    loadingEntitlements: isLoadingEntitlements,
    maxEntitlementsCount: safeMaxEntitlementsCount,
    perVisitLimit,
    remainingEntitlementsCount,
    usedEntitlementsCount,
  };
};

export default useGetEntitlements;
